/**
 * @fileoverview Search field component with search icon and clear button
 * @module ctl-search-field
 * @version 1.0.0
 * 
 * @description
 * A specialized text input for search functionality with search icon,
 * clear button, and optional debouncing.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-search-field placeholder="Search..."></ctl-search-field>
 * 
 * @example
 * <!-- With debounce -->
 * <ctl-search-field 
 *   placeholder="Search products" 
 *   debounce="300"
 * ></ctl-search-field>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    --ctl-radius: var(--border-radius-md, 8px);

    --ctl-color-focus: var(--ctl-color-brand);

    --ctl-field-height: var(--input-height, 40px);
    --ctl-pad-x: var(--spacing-sm, 12px);
    --ctl-gap: var(--spacing-xs, 8px);

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1, var(--spacing-xxs, 4px));
  }

  .label {
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--font-weight-normal, 400);
    color: var(--ctl-color-text);
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--ctl-gap);
    height: var(--ctl-field-height);
    padding: 0 var(--ctl-pad-x);
    border-radius: var(--ctl-radius);
    border: var(--border-width-thin, 1px) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--transition-fast, 150ms ease-in-out), box-shadow var(--transition-fast, 150ms ease-in-out), background var(--transition-fast, 150ms ease-in-out), opacity var(--transition-fast, 150ms ease-in-out);
  }

  .input-wrapper:focus-within {
    border-color: var(--ctl-color-focus);
    box-shadow: var(--ctl-focus-ring, var(--ctl-shadow-sm));
  }

  :host([disabled]) .input-wrapper {
    opacity: var(--opacity-60, 0.6);
    background: var(--ctl-color-surface-2, color-mix(in srgb, var(--color-black) 2%, transparent));
    cursor: not-allowed;
  }

  .search-icon {
    display: inline-flex;
    align-items: center;
    color: var(--ctl-color-muted);
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: var(--font-size-sm, 0.875rem);
    color: var(--ctl-color-text);
    outline: none;
    padding: 0;
    min-width: 0;
  }

  input::placeholder {
    color: var(--ctl-color-muted);
  }

  input:disabled {
    cursor: not-allowed;
  }

  .clear-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--spacing-lg, 20px);
    height: var(--spacing-lg, 20px);
    border: none;
    background: transparent;
    color: var(--ctl-color-muted);
    cursor: pointer;
    border-radius: 50%;
    padding: 0;
    font-size: var(--font-size-md, 1rem);
    line-height: 1;
    transition: background var(--transition-fast, 150ms ease-in-out), color var(--transition-fast, 150ms ease-in-out);
    flex-shrink: 0;
  }

  .clear-button:hover {
    background: color-mix(in srgb, var(--ctl-color-text) 5%, transparent);
    color: var(--ctl-color-text);
  }

  .clear-button:hidden {
    display: none;
  }

  :host([disabled]) .clear-button {
    cursor: not-allowed;
    opacity: var(--opacity-50, 0.5);
  }

  .help {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
  }
</style>

<div class="container">
  <label class="label" part="label"></label>
  <div class="input-wrapper" part="input-wrapper">
    <span class="search-icon" part="search-icon">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
    </span>
    <input type="search" part="input" />
    <button type="button" class="clear-button" part="clear-button" aria-label="Clear search" hidden>×</button>
  </div>
  <div class="help" part="help"></div>
</div>
`;

/**
 * Search Field Web Component
 * 
 * @class CtlSearchField
 * @extends HTMLElement
 * 
 * @attr {string} label - Label text for the search field
 * @attr {string} value - Current search value
 * @attr {string} placeholder - Placeholder text for the input
 * @attr {string} help - Help text displayed below the input
 * @attr {number} debounce - Debounce delay in milliseconds (default: 0)
 * @attr {boolean} disabled - Whether the search field is disabled
 * @attr {string} name - Name attribute for form submission
 * 
 * @fires ctl-search - Dispatched when search value changes (respects debounce)
 * @fires ctl-search.detail.value - The search query value
 * @fires ctl-input - Dispatched immediately on input (no debounce)
 * @fires ctl-input.detail.value - The current input value
 * @fires ctl-clear - Dispatched when search is cleared
 * 
 * @csspart label - The label element
 * @csspart input-wrapper - The input wrapper container
 * @csspart search-icon - The search icon element
 * @csspart input - The search input element
 * @csspart clear-button - The clear button
 * @csspart help - The help text element
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color for icons
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-focus - Focus state color
 * @cssprop --ctl-field-height - Input field height
 * @cssprop --ctl-radius - Border radius
 */
export class CtlSearchField extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._input = this.shadowRoot.querySelector('input');
    this._clearButton = this.shadowRoot.querySelector('.clear-button');
    this._label = this.shadowRoot.querySelector('.label');
    this._help = this.shadowRoot.querySelector('.help');
    this._debounceTimer = null;
    
    // Bind methods once in constructor to maintain reference for cleanup
    this._boundHandleInput = this._handleInput.bind(this);
    this._boundHandleClear = this._handleClear.bind(this);
  }

  static get observedAttributes() {
    return ['label', 'value', 'placeholder', 'help', 'debounce', 'disabled', 'name'];
  }

  // Track whether the deprecation warning has already been shown
  static _deprecationWarned = false;

  connectedCallback() {
    if (!CtlSearchField._deprecationWarned) {
      CtlSearchField._deprecationWarned = true;
      if (typeof console !== 'undefined' && console && typeof console.warn === 'function') {
        console.warn(
          '[ctl-search-field] Deprecated: use <ctl-text-field type="search"> with a prefix slot for the search icon instead. ' +
          'ctl-search-field will be removed in a future major version.'
        );
      }
    }
    this._input.addEventListener('input', this._boundHandleInput);
    this._clearButton.addEventListener('click', this._boundHandleClear);
    this._render();
  }

  disconnectedCallback() {
    this._input.removeEventListener('input', this._boundHandleInput);
    this._clearButton.removeEventListener('click', this._boundHandleClear);
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this._render();
    }
  }

  _handleInput(e) {
    const value = e.target.value;
    this.setAttribute('value', value);
    
    // Show/hide clear button
    this._clearButton.hidden = !value;

    // Dispatch immediate input event
    this.dispatchEvent(new CustomEvent('ctl-input', {
      bubbles: true,
      composed: true,
      detail: { value }
    }));

    // Handle debounced search event
    const debounce = parseInt(this.getAttribute('debounce')) || 0;
    
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }

    if (debounce > 0) {
      this._debounceTimer = setTimeout(() => {
        this._dispatchSearch(value);
      }, debounce);
    } else {
      this._dispatchSearch(value);
    }
  }

  _handleClear(e) {
    if (this.hasAttribute('disabled')) return;
    e.stopPropagation();
    
    this._input.value = '';
    this.removeAttribute('value');
    this._clearButton.hidden = true;
    this._input.focus();
    
    this.dispatchEvent(new CustomEvent('ctl-clear', {
      bubbles: true,
      composed: true
    }));
    
    this._dispatchSearch('');
  }

  _dispatchSearch(value) {
    this.dispatchEvent(new CustomEvent('ctl-search', {
      bubbles: true,
      composed: true,
      detail: { value }
    }));
  }

  _render() {
    // Label
    const label = this.getAttribute('label');
    if (label) {
      this._label.textContent = label;
      this._label.style.display = 'block';
    } else {
      this._label.style.display = 'none';
    }

    // Value
    const value = this.getAttribute('value');
    if (value !== null && this._input.value !== value) {
      this._input.value = value;
      this._clearButton.hidden = !value;
    }

    // Placeholder
    const placeholder = this.getAttribute('placeholder');
    if (placeholder) {
      this._input.placeholder = placeholder;
    }

    // Name
    const name = this.getAttribute('name');
    if (name) {
      this._input.name = name;
    }

    // Disabled
    this._input.disabled = this.hasAttribute('disabled');
    this._clearButton.disabled = this.hasAttribute('disabled');

    // Help text
    const help = this.getAttribute('help');
    if (help) {
      this._help.textContent = help;
      this._help.style.display = 'block';
    } else {
      this._help.style.display = 'none';
    }
  }

  /**
   * Get the current search value
   * @returns {string} The current search value
   */
  get value() {
    return this._input.value;
  }

  /**
   * Set the search value
   * @param {string} val - The search value
   */
  set value(val) {
    this.setAttribute('value', val);
  }

  /**
   * Clear the search field
   */
  clear() {
    this._handleClear(new Event('click'));
  }

  /**
   * Focus the search field
   */
  focus() {
    this._input.focus();
  }
}

if (!customElements.get("ctl-search-field")) {
  customElements.define("ctl-search-field", CtlSearchField);
}
